import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { toast } from 'react-toastify';

import ApiService from '../../services/apiService';
import { Filters } from '../../interfaces/filters';
import { Shipper, ShippersResponse } from '../../interfaces/services/shippers';
import { Order, OrderResponse } from '../../interfaces/services/orders';
import { ApiStatus, EquipmentTypeCode, Grade } from '../../utils/enums';
import { getTTCBaseUrl } from '../../utils/environmentUtils';

interface ShipperState {
    shipperDetailsApiStatus: ApiStatus;
    shipperDetails: Shipper;
    ordersApiStatus: ApiStatus;
    orders: Order[];
    orderCount: number;
}

/** Thunks */
export const fetchShipperDetails = createAsyncThunk(
    'shippers/fetchShipperDetails',
    async (
        {
            filter_criteria,
        }: {
            filter_criteria?: Filters;
        },
        thunkApi
    ) => {
        try {
            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/pickup-parties`,
                body: {
                    filter_criteria,
                },
            }) as AxiosResponse<ShippersResponse>;

            if (data.pickup_parties.length === 0) {
                return thunkApi.rejectWithValue(data);
            }

            return data;
        } catch (error) {
            console.error('Unable to load shipper details', error);
            toast.error('Unable to load shipper details');
            throw error;
        }
    }
);

export const fetchOrders = createAsyncThunk(
    'shipper/fetchOrders',
    async ({
        mri_freight_order_specified_eqpt_code,
        filter_criteria,
    }: {
        mri_freight_order_specified_eqpt_code: EquipmentTypeCode;
        filter_criteria: Filters;
    }) => {
        try {
            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/orders`,
                body: {
                    filter_criteria: {
                        ...filter_criteria,
                        mri_freight_order_specified_eqpt_code,
                    },
                },
            }) as AxiosResponse<OrderResponse>;

            return data;
        } catch (error) {
            console.error('Unable to load orders for shipper', error);
            toast.error('Unable to load orders for shipper');
            throw error;
        }
    }
);

const initialState: ShipperState = {
    shipperDetailsApiStatus: ApiStatus.Idle,
    shipperDetails: {
        pickup_party_id: '',
        pickup_party_name: '',
        pickup_party_health_grade: Grade.E,
        freight_order_count: null,
        freight_order_count_grade: Grade.E,
        freight_order_count_rank: null,
        commodity_codes: [],
        total_revenue_usd_amount: null,
        linehaul_revenue_usd_amount: null,
        fuel_surcharge_usd_amount: null,
        accessorial_revenue_usd_amount: null,
        travel_distance_miles_amount: null,
        billable_distance_miles_amount: null,
        loaded_distance_miles_amount: null,
        empty_distance_miles_amount: null,
        pta_to_last_stop_departure_hours_duration: null,
        dispatch_to_last_stop_departure_hours_duration: null,
        first_stop_to_last_stop_hours_duration: null,
        first_stop_departure_to_final_stop_arrival_hours_duration: null,
        standard_transit_hours_duration: null,
        netwise_dwell_hours_duration: null,
        netwise_in_transit_delay_hours_duration: null,
        average_netwise_in_transit_delay_hours_duration: null,
        average_netwise_in_transit_delay_hours_ratio: null,
        average_netwise_in_transit_delay_hours_ratio_grade: Grade.E,
        average_netwise_in_transit_delay_hours_ratio_rank: null,
        netwise_delay_hours_duration: null,
        load_hours_duration: null,
        average_load_hours_duration: null,
        average_load_hours_duration_grade: Grade.E,
        average_load_hours_duration_rank: null,
        unload_hours_duration: null,
        empty_transit_hours_duration: null,
        average_scheduled_miles_per_hour: null,
        average_actual_miles_per_hour: null,
        average_scheduled_vs_actual_miles_per_hour: null,
        average_scheduled_vs_actual_miles_per_hour_grade: Grade.E,
        average_scheduled_vs_actual_miles_per_hour_rank: null,
        linehaul_rate_usd_amount: null,
        average_pickup_party_order_yield_usd: null,
        average_loaded_distance_miles_amount: null,
    },
    ordersApiStatus: ApiStatus.Idle,
    orders: [],
    orderCount: 0,
};

export const shipperDetailsSlice = createSlice({
    name: 'shipper',
    initialState,
    reducers: {
        clearShipperDetailsData: (): ShipperState => {
            return initialState;
        },
    },
    extraReducers(builder) {
        builder.addCase(fetchShipperDetails.pending, (state) => {
            state.shipperDetailsApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchShipperDetails.fulfilled, (state, action) => {
            state.shipperDetailsApiStatus = ApiStatus.Success;
            [state.shipperDetails] = action.payload.pickup_parties;
        });
        builder.addCase(fetchShipperDetails.rejected, (state) => {
            state.shipperDetailsApiStatus = ApiStatus.Failure;
            state.shipperDetails = initialState.shipperDetails;
        });

        builder.addCase(fetchOrders.pending, (state) => {
            state.ordersApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchOrders.fulfilled, (state, action) => {
            state.ordersApiStatus = ApiStatus.Success;
            state.orders = action.payload.freight_orders;
            state.orderCount = action.payload.freight_order_count;

            if (action.payload.freight_orders.length === 0) {
                toast.info("We didn't find any orders on this shipper that match your search criteria. Try adjusting your filters");
            }
        });
        builder.addCase(fetchOrders.rejected, (state) => {
            state.ordersApiStatus = ApiStatus.Failure;
            state.orders = initialState.orders;
            state.orderCount = initialState.orderCount;
        });
    },
});

export default shipperDetailsSlice.reducer;

export const {
    clearShipperDetailsData,
} = shipperDetailsSlice.actions;
